import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Box, CircularProgress, Snackbar, Alert, Button } from '@mui/material';
import { AppDispatch } from '../store/store';
import { fetchCustomerProfile, logoutCustomer } from '../store/slices/customerAuthSlice';
import { AxiosError } from 'axios';
import { useTokenExpiration, isTokenExpired, TOKEN_EXPIRY_KEY, useRefreshState } from '../hooks/useTokenExpiration';

interface AuthInitializerProps {
  children: React.ReactNode;
}

// Timeout for auth initialization (10 seconds)
const AUTH_INIT_TIMEOUT = 10000;

/**
 * AuthInitializer restores customer authentication state on app startup.
 * With httpOnly cookie authentication, tokens are NOT accessible via JavaScript.
 * Instead, we check if a valid session exists by fetching the user profile.
 * Also monitors token expiration (via localStorage expiry timestamp) and shows warnings.
 */
const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isInitializing, setIsInitializing] = useState(true);
  const [showExpirationWarning, setShowExpirationWarning] = useState(false);
  const [showExpiredNotice, setShowExpiredNotice] = useState(false);
  const [showRefreshError, setShowRefreshError] = useState(false);
  const hasInitialized = useRef(false);

  // Task #4377: Track token refresh state for UI feedback
  const { isRefreshing, refreshError } = useRefreshState();

  // Callback for token expiration warning (5 minutes before expiry)
  const handleExpirationWarning = useCallback(() => {
    setShowExpirationWarning(true);
  }, []);

  // Callback for token expiration (triggers auto-logout)
  const handleTokenExpired = useCallback(() => {
    setShowExpiredNotice(true);
  }, []);

  // Use token expiration monitoring hook
  useTokenExpiration(handleExpirationWarning, handleTokenExpired);

  useEffect(() => {
    // Prevent re-initialization (strict mode double-mount protection)
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    const initializeAuth = async () => {
      // With httpOnly cookies, we can't check if token exists via JavaScript.
      // We check token expiry time (stored in localStorage) to determine if we should try auth.
      // If no expiry stored, we still try to fetch profile (browser will send cookie if exists).
      const expiryString = localStorage.getItem(TOKEN_EXPIRY_KEY);

      // If we have expiry info and token is expired, skip server call
      if (expiryString && isTokenExpired()) {
        console.info('Token expired on startup, clearing session');
        dispatch(logoutCustomer());
        setIsInitializing(false);
        return;
      }

      // Set timeout to prevent indefinite loading
      const timeoutId = setTimeout(() => {
        console.warn('Auth initialization timed out');
        setIsInitializing(false);
      }, AUTH_INIT_TIMEOUT);

      try {
        // Try to validate session by fetching profile
        // Browser will automatically send httpOnly cookie if it exists
        await dispatch(fetchCustomerProfile()).unwrap();
      } catch (error) {
        // Only logout for auth errors (401, 403), not network issues
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          if (status === 401 || status === 403) {
            // Session invalid - clear any stored expiry
            dispatch(logoutCustomer());
          }
          // For network errors (no response), don't logout - user can retry
        } else if (typeof error === 'string' && (error.includes('401') || error.includes('403') || error.includes('unauthorized'))) {
          // Handle rejectWithValue string errors
          dispatch(logoutCustomer());
        }
        // For other errors, silently fail - no session, user needs to login
      } finally {
        clearTimeout(timeoutId);
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, [dispatch]);

  // Close expiration warning notification
  const handleCloseWarning = () => {
    setShowExpirationWarning(false);
  };

  // Close expired notice notification
  const handleCloseExpiredNotice = () => {
    setShowExpiredNotice(false);
  };

  // Close refresh error notification (Task #4377)
  const handleCloseRefreshError = () => {
    setShowRefreshError(false);
  };

  // Handle re-login action from refresh error (Task #4377)
  const handleReLogin = () => {
    setShowRefreshError(false);
    dispatch(logoutCustomer());
    // Navigation to login page happens via ProtectedRoute
  };

  // Task #4377: Show refresh error notification when refresh fails
  useEffect(() => {
    if (refreshError) {
      setShowRefreshError(true);
    }
  }, [refreshError]);

  // Show loading spinner while initializing auth
  if (isInitializing) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {children}

      {/* Token expiration warning notification (5 minutes before expiry) */}
      <Snackbar
        open={showExpirationWarning}
        autoHideDuration={30000}
        onClose={handleCloseWarning}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseWarning} severity="warning" sx={{ width: '100%' }}>
          Your session will expire in 5 minutes. Please save your work.
        </Alert>
      </Snackbar>

      {/* Token expired notification */}
      <Snackbar
        open={showExpiredNotice}
        autoHideDuration={6000}
        onClose={handleCloseExpiredNotice}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseExpiredNotice} severity="info" sx={{ width: '100%' }}>
          Your session has expired. Please log in again.
        </Alert>
      </Snackbar>

      {/* Task #4377: Token refresh in progress indicator - bottom-right to avoid overlap */}
      <Snackbar
        open={isRefreshing}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity="info"
          icon={<CircularProgress size={20} />}
          sx={{ width: '100%' }}
        >
          Refreshing session...
        </Alert>
      </Snackbar>

      {/* Task #4377: Token refresh error notification with re-login option - top-right to avoid overlap */}
      <Snackbar
        open={showRefreshError}
        autoHideDuration={10000}
        onClose={handleCloseRefreshError}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseRefreshError}
          severity="error"
          sx={{ width: '100%' }}
          action={
            <Button color="inherit" size="small" onClick={handleReLogin}>
              Re-login
            </Button>
          }
        >
          {refreshError || 'Session refresh failed. Please log in again.'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AuthInitializer;
